/**
 * De onde veio uma dimensão da classificação.
 *
 * Espelho de `core.vocabulary.THEME_ASSIGNMENT_SOURCES` (origem do tema:
 * regra de cadastro por publicador ou veículo, coluna da planilha, termo
 * cadastrado ou IA) e de `core.vocabulary.CLASSIFICATION_SOURCES` (origem
 * da polaridade, da abordagem e das mensagens-chave: planilha, termo
 * cadastrado ou IA). Paridade travada por
 * `tests/test_classification_sources_parity.py`, que lê este arquivo —
 * a ORDEM das tuplas também conta.
 *
 * `null` na origem não é um valor do vocabulário: é histórico anterior ao
 * registro de origem (sem backfill) ou valor posto à mão — a correção
 * manual zera a origem da dimensão que mudou.
 *
 * Duas funções de rótulo, de propósito: `themeSourceLabel` é a NOTA do
 * chip do tema e só fala das origens que não vieram do texto (publicador,
 * veículo, planilha) — anotar "pela IA" em todo chip seria ruído —, e
 * `sourceLabel` é o rótulo completo de qualquer origem, usado onde a
 * origem é a pergunta (o "por que a classificação é esta" do detalhe).
 */

export const THEME_SOURCES = ["publisher", "vehicle", "imported", "term", "ai"] as const;

export type ThemeSource = (typeof THEME_SOURCES)[number];

export const THEME_SOURCE_LABEL: Record<ThemeSource, string> = {
  publisher: "atribuído pelo publicador",
  vehicle: "atribuído pelo veículo",
  imported: "importado da planilha",
  term: "pelo termo cadastrado",
  ai: "pela IA"
};

export const CLASSIFICATION_SOURCES = ["imported", "term", "ai"] as const;

export type ClassificationSource = (typeof CLASSIFICATION_SOURCES)[number];

export const CLASSIFICATION_SOURCE_LABEL: Record<ClassificationSource, string> = {
  imported: "importada da planilha",
  term: "pelo termo cadastrado",
  ai: "pela IA"
};

/** As origens que o chip do tema anota entre parênteses: as que NÃO
 *  vieram do texto da matéria. Termo e IA são o caso comum e ficam sem
 *  nota. */
const NOTED_THEME_SOURCES: readonly ThemeSource[] = ["publisher", "vehicle", "imported"];

export function isThemeSource(value: string): value is ThemeSource {
  return (THEME_SOURCES as readonly string[]).includes(value);
}

export function isClassificationSource(value: string): value is ClassificationSource {
  return (CLASSIFICATION_SOURCES as readonly string[]).includes(value);
}

/** A nota do chip do tema. Origem desconhecida não inventa rótulo: a
 *  pessoa não vê nada, em vez de ver uma explicação errada. */
export function themeSourceLabel(source: string | null | undefined): string | undefined {
  return source && isThemeSource(source) && NOTED_THEME_SOURCES.includes(source)
    ? THEME_SOURCE_LABEL[source]
    : undefined;
}

/** O rótulo completo de qualquer origem de TEMA, inclusive termo e IA. */
export function themeSourceFullLabel(source: string | null | undefined): string | undefined {
  return source && isThemeSource(source) ? THEME_SOURCE_LABEL[source] : undefined;
}

/** O rótulo completo de qualquer origem de polaridade, abordagem ou
 *  mensagem-chave. */
export function sourceLabel(source: string | null | undefined): string | undefined {
  return source && isClassificationSource(source) ? CLASSIFICATION_SOURCE_LABEL[source] : undefined;
}
